// Small shared widgets: countdown, guild emblem, job icon, copy button, markdown, Turnstile,
// auth guard. Kept together because each is a few lines.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { jobName } from '@shared/jobs';
import { clock } from '@/lib/format';
import { useNow } from '@/lib/hooks';
import { useConfig, useSession } from '@/lib/session';
import { IconCheck, IconCopy } from './icons';
import { cx, Loading } from './ui';

export function Countdown({ to, className }: { to: string | number; className?: string }) {
  const now = useNow(1000);
  const ms = new Date(to).getTime() - now;
  return <span className={cx('num', className)}>{clock(ms)}</span>;
}

/** A guild emblem (decoded by /api/emblem), or an empty tile for a guild without one.
 *  Emblems are 48x48 now (older ones 24x24): crisp pixels when drawn larger
 *  than the image, smooth when drawn smaller, where nearest-neighbour drops rows. */
export function Emblem({ url, size = 24 }: { url: string | null; size?: number }) {
  const [natural, setNatural] = useState(0);
  return url ? (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      className={cx('shrink-0', natural > size ? '[image-rendering:auto]' : '[image-rendering:pixelated]')}
      style={{ width: size, height: size }}
      onLoad={(e) => setNatural(e.currentTarget.naturalWidth)}
    />
  ) : (
    <span className="inline-block shrink-0 border border-line bg-panel-3" style={{ width: size, height: size }} aria-hidden />
  );
}

/** Party-window job icon (from the client's own UI art) or a letter tile when icons are off. */
export function JobIcon({ id, size = 28, className }: { id: number; size?: number; className?: string }) {
  const config = useConfig();
  const [broken, setBroken] = useState(false);
  const name = jobName(id);
  if (!config.showGameIcons || broken) {
    return (
      <span
        className={cx('inline-flex shrink-0 items-center justify-center border border-line bg-panel-3 font-display text-gold', className)}
        style={{ width: size, height: size, fontSize: size * 0.45 }}
        title={name}
        aria-hidden
      >
        {name.charAt(0)}
      </span>
    );
  }
  return (
    <img
      src={`/icons/jobs/${id}.webp`}
      alt=""
      title={name}
      width={size}
      height={size}
      loading="lazy"
      onError={() => setBroken(true)}
      className={cx('shrink-0 [image-rendering:pixelated]', className)}
      style={{ width: size, height: size }}
    />
  );
}

export function ItemIcon({ id, size = 36, className }: { id: number; size?: number; className?: string }) {
  const config = useConfig();
  const [broken, setBroken] = useState(false);
  if (!config.showGameIcons || broken) {
    return <span className={cx('inline-block shrink-0 border border-line bg-panel-3', className)} style={{ width: size, height: size }} aria-hidden />;
  }
  return (
    <img
      src={`/icons/items/${id}.webp`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      onError={() => setBroken(true)}
      className={cx('shrink-0', className)}
      style={{ width: size, height: size }}
    />
  );
}

export function CopyButton({ text, label = 'Copy', className }: { text: string; label?: string; className?: string }) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!done) return;
    const t = setTimeout(() => setDone(false), 1800);
    return () => clearTimeout(t);
  }, [done]);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => setDone(true));
      }}
      className={cx('inline-flex items-center gap-1.5 border border-line px-2.5 py-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted transition-colors hover:border-gold hover:text-gold-bright', className)}
      aria-label={done ? 'Copied' : `${label}: ${text}`}
    >
      {done ? <IconCheck size={14} /> : <IconCopy size={14} />}
      {done ? 'Copied' : label}
    </button>
  );
}

// ---- Cloudflare Turnstile ----------------------------------------------------------

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id: string) => void;
    };
  }
}

let turnstileScript: Promise<void> | null = null;
function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  turnstileScript ??= new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('turnstile failed to load'));
    document.head.appendChild(s);
  });
  return turnstileScript;
}

/** Renders the "I am human" check when the site has a Turnstile key; otherwise nothing (dev). */
/**
 * The "I am human" check. `action` is bound into the token and checked by the
 * server (auth/turnstile.ts), so a token solved here is useless on any other
 * form. Tokens are single-use: bump `resetKey` after a failed submit so the
 * player gets a fresh one without reloading.
 */
export function Turnstile({
  onToken,
  resetKey,
  action,
}: {
  onToken: (t: string | undefined) => void;
  resetKey?: number;
  action: 'login' | 'register' | 'forgot_password' | 'partner_apply' | 'reseller_report';
}) {
  const { turnstileSiteKey } = useConfig();
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  useEffect(() => {
    if (!turnstileSiteKey || !box.current) return;
    let cancelled = false;
    loadTurnstile()
      .then(() => {
        if (cancelled || !box.current || !window.turnstile) return;
        widget.current = window.turnstile.render(box.current, {
          sitekey: turnstileSiteKey,
          action,
          theme: 'dark',
          callback: (t: string) => onToken(t),
          'expired-callback': () => onToken(undefined),
          'error-callback': () => onToken(undefined),
        });
      })
      .catch(() => onToken(undefined));
    return () => {
      cancelled = true;
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turnstileSiteKey, resetKey]);
  if (!turnstileSiteKey) return null;
  return <div ref={box} className="min-h-[65px]" />;
}

/** Wraps pages that need a logged-in player; sends others to /login and back. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  if (session.isPending) return <Loading />;
  if (!session.data?.user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
}
